import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getPublicMp2Catalog,
  type PublicMp2Catalog,
} from "@/lib/public/meal-plans-v2.functions";

export type PublicMp2CatalogState = {
  catalog: PublicMp2Catalog | undefined;
  isLoading: boolean;
  /**
   * Mode-based visibility for the customer-facing MP2 surface.
   * OFF     → hidden for everyone.
   * TEST    → visible only to admins.
   * PREVIEW → visible to the public; ordering is disabled and Step 3
   *           swaps to the "notify me" interest form.
   * LIVE    → fully visible + orderable.
   */
  isMp2Visible: boolean;
  mode: "off" | "test" | "preview" | "live";
  isPreview: boolean;
  isAdmin: boolean;
};

export function usePublicMp2Catalog(): PublicMp2CatalogState {
  const fetchFn = useServerFn(getPublicMp2Catalog);
  const q = useQuery({
    queryKey: ["mp2-public-catalog"],
    queryFn: () => fetchFn(),
    staleTime: 60_000,
  });
  const catalog = q.data;
  const mode = (catalog?.mode ?? "off") as PublicMp2CatalogState["mode"];
  const isAdmin = Boolean(catalog?.is_admin);
  const unavailable = Boolean(catalog?.unavailable);
  const isMp2Visible =
    !unavailable &&
    (mode === "live" || mode === "preview" || (mode === "test" && isAdmin));
  const isPreview = mode === "preview" && !isAdmin;
  return { catalog, isLoading: q.isLoading, isMp2Visible, mode, isPreview, isAdmin };
}
